import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import { Avatar } from "../components/Avatar.js";
import { ThemeToggle } from "../components/ThemeToggle.js";
import { useAuth } from "../auth/AuthContext.js";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", end: true, adminOnly: false },
  { to: "/connectors", label: "Connectors", end: false, adminOnly: false },
  { to: "/users", label: "Users", end: false, adminOnly: true },
  { to: "/approvals", label: "Approvals", end: false, adminOnly: true },
  { to: "/conflicts", label: "Conflicts", end: false, adminOnly: false },
  { to: "/webhooks", label: "Webhooks", end: false, adminOnly: true },
  { to: "/audit", label: "Audit Log", end: false, adminOnly: true },
];

export function TopNav() {
  const { user } = useAuth();
  const items = NAV_ITEMS.filter((i) => !i.adminOnly || user?.isAdmin);

  return (
    <nav className="bg-surface-container/60 backdrop-blur-lg border-b border-outline-variant/40 shadow-xl shadow-black/20 sticky top-0 z-50 flex items-center justify-between px-6 h-16 w-full font-sans antialiased tracking-tight text-sm">
      <div className="flex items-center gap-8">
        <NavLink to="/" end className="flex items-center gap-2 text-primary-fixed-dim">
          <Icon name="sync" className="text-2xl" />
          <span className="text-lg font-black tracking-tighter uppercase">AppsDoCalSync</span>
        </NavLink>
        <div className="hidden md:flex gap-6 items-center h-16">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive
                  ? "text-primary-fixed-dim border-b-2 border-primary-fixed-dim h-16 flex items-center transition-all"
                  : "text-on-surface-variant hover:text-on-surface transition-colors h-16 flex items-center"
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ThemeToggle />
        <button
          type="button"
          className="p-2 hover:bg-surface-container rounded-md transition-all active:scale-[0.98] text-on-surface-variant"
          aria-label="Notifications"
        >
          <Icon name="notifications" />
        </button>
        <UserMenu />
      </div>
    </nav>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  async function onSignOut() {
    setOpen(false);
    await signOut();
    navigate("/login", { replace: true });
  }

  const displayName = user.name?.trim() || user.email;

  return (
    <div className="relative ml-2" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 hover:bg-surface-container rounded-md px-1 py-1 transition-all active:scale-[0.98]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={displayName} size="sm" tone="primary" />
        <span className="hidden md:inline text-body-sm text-on-surface max-w-[160px] truncate">
          {displayName}
        </span>
        <Icon name="expand_more" className="text-on-surface-variant text-[18px]" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-64 bg-surface-container border border-outline-variant rounded-lg shadow-2xl shadow-black/40 overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-outline-variant">
            <p className="text-body-sm text-on-surface font-semibold truncate">{displayName}</p>
            <p className="text-label-xs font-mono text-on-surface-variant truncate">{user.email}</p>
            {user.isAdmin && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-primary-container/20 text-primary-fixed-dim text-[10px] font-mono uppercase rounded-full">
                Admin
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="w-full text-left px-4 py-3 text-body-sm text-on-surface hover:bg-surface-container-high transition-colors flex items-center gap-2"
          >
            <Icon name="logout" className="text-[18px] text-on-surface-variant" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
